import { useTranslation } from 'react-i18next'
import {
	Avatar, AvatarFallback, AvatarImage,
	Badge,
	Button,
	Checkbox,
	Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@rentflowdev/ui'
import { useGetTaskQuery } from '@/api'
import { DEFAULT_LOCALE } from '@/types'
import type { Task } from '@/types'

function formatDue(iso: string): string {
	const d = new Date(iso)
	return `${d.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })}`
}

function avatarInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return ''
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

interface TaskDetailSheetProps {
	taskId:        string | null
	onClose:       () => void
	onEdit?:       (task: Task) => void
	onDelete?:     (task: Task) => void
	onToggleDone?: (task: Task) => void
}

export function TaskDetailSheet({ taskId, onClose, onEdit, onDelete, onToggleDone }: TaskDetailSheetProps) {
	const { t } = useTranslation()
	const { data: task } = useGetTaskQuery(taskId!, { skip: !taskId })

	return (
		<Sheet open={!!taskId} onOpenChange={(v) => { if (!v) onClose() }}>
			<SheetContent side="right" className="w-[420px] flex flex-col gap-0 p-0">
				<SheetHeader className="border-b border-(--color-border) px-6 py-4">
					<SheetTitle>{t('tasks.detail.title')}</SheetTitle>
				</SheetHeader>
				{task && (
					<div className="flex flex-col gap-6 overflow-y-auto px-6 py-5">
						<div className="flex items-start gap-3">
							{onToggleDone && (
								<Checkbox
									checked={task.isDone}
									onCheckedChange={() => onToggleDone(task)}
									className="mt-0.5"
								/>
							)}
							<div className="flex flex-col gap-1">
								{task.title && (
									<p className="text-sm font-[500] leading-relaxed text-(--color-foreground)">{task.title}</p>
								)}
								<p className="text-sm leading-relaxed text-(--color-foreground)">{task.description}</p>
							</div>
						</div>
						<dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-3 text-sm">
							<dt className="text-(--color-muted-foreground)">{t('tasks.columns.kind')}</dt>
							<dd>{t(`tasks.kinds.${task.kind}`)}</dd>

							{task.isOverdue && !task.isDone && (
								<>
									<dt className="text-(--color-muted-foreground)">{t('tasks.detail.status')}</dt>
									<dd>
										<Badge variant="destructive" size="sm">{t('tasks.priorities.OVERDUE')}</Badge>
									</dd>
								</>
							)}

							<dt className="text-(--color-muted-foreground)">{t('tasks.columns.due')}</dt>
							<dd className={task.isOverdue && !task.isDone ? 'text-(--color-destructive)' : ''}>
								{task.dueDate ? formatDue(task.dueDate) : '—'}
							</dd>

							<dt className="text-(--color-muted-foreground)">{t('tasks.columns.responsible')}</dt>
							<dd>
								{task.responsibleName ? (
									<div className="flex items-center gap-2">
										<Avatar className="size-5">
											{task.responsibleAvatar && <AvatarImage src={task.responsibleAvatar} />}
											<AvatarFallback className="text-xs">{avatarInitials(task.responsibleName)}</AvatarFallback>
										</Avatar>
										<span>{task.responsibleName}</span>
									</div>
								) : '—'}
							</dd>

							{task.binding && (
								<>
									<dt className="text-(--color-muted-foreground)">{t('tasks.columns.binding')}</dt>
									<dd className="flex flex-col gap-0.5">
										{task.binding.title && (
											<span className="font-[500] text-(--color-info)">{task.binding.title}</span>
										)}
										{task.binding.subtitle && (
											<span className="text-(--color-muted-foreground)">{task.binding.subtitle}</span>
										)}
									</dd>
								</>
							)}

							<dt className="text-(--color-muted-foreground)">{t('tasks.detail.createdAt')}</dt>
							<dd className="text-(--color-muted-foreground)">
								{new Date(task.createdAt).toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })}
							</dd>
						</dl>

						{(onEdit || onDelete) && (
							<div className="flex gap-2">
								{onEdit && (
									<Button size="sm" variant="secondary" onClick={() => onEdit(task)}>
										{t('tasks.actions.edit')}
									</Button>
								)}
								{onDelete && (
									<Button size="sm" variant="ghost" className="text-(--color-destructive)" onClick={() => onDelete(task)}>
										{t('tasks.actions.delete')}
									</Button>
								)}
							</div>
						)}
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}
